import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Header from '../layout/header';
import Footer from '../layout/footer';
import { getLocalItem, getSessionItem } from '../../utils/storage';

import {
  PremiumHero,
  StickyMobileCTA,
  SectionWrapper,
  FinalCTA,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

import brandsData from '../../data/brands_data.json';
import { startingPriceLabel } from '../../utils/pricing';

const SITE_FROM = startingPriceLabel('site');

class BrandsIndex extends Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      filter: 'all', // 'all' | 'bike' | 'car'
    };
  }

  componentDidMount() {
    const { setOpenModal, isMobileApp, history } = this.props;
    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      this.props.user.is_logged
    ) {
      setOpenModal(false);
    }
    if (isMobileApp) {
      history.push('/register');
    }
  }

  tryRequireLogo(logoFile) {
    if (!logoFile) return null;
    try {
      return require(`../../images/brand_logos/${logoFile}`);
    } catch (e) {
      return null;
    }
  }

  getFilteredBrands() {
    const { search, filter } = this.state;
    const q = search.toLowerCase().trim();

    let bikes = brandsData.bike || [];
    let cars = brandsData.car || [];

    if (filter === 'bike') cars = [];
    if (filter === 'car') bikes = [];

    if (q) {
      bikes = bikes.filter((b) => b.label.toLowerCase().includes(q));
      cars = cars.filter((b) => b.label.toLowerCase().includes(q));
    }

    return { bikes, cars };
  }

  renderBrandCard(brand) {
    const logo = this.tryRequireLogo(brand.logo);
    return (
      <Link key={brand.slug} to={`/brands/${brand.slug}`} className="rr-brand-card">
        {logo ? (
          <img
            src={typeof logo === 'object' ? logo.default : logo}
            alt={`${brand.label} logo`}
            className="rr-brand-card__logo"
            loading="lazy"
          />
        ) : (
          <div className="rr-brand-card__logo" style={{ fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {brand.label.charAt(0)}
          </div>
        )}
        <span className="rr-brand-card__label">{brand.label}</span>
        <span className="rr-brand-card__count">{brand.models.length} model{brand.models.length !== 1 ? 's' : ''}</span>
      </Link>
    );
  }

  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, setCitiesModal } = this.props;
    const { search, filter } = this.state;
    const { bikes, cars } = this.getFilteredBrands();

    const totalBrands = (brandsData.bike || []).length + (brandsData.car || []).length;
    const canonical = 'https://www.ridenrepair.com/brands';

    const schemas = [
      {
        '@type': 'ItemList',
        name: 'Vehicle Brands We Service',
        description: `${totalBrands}+ bike and car brands serviced by Ride N Repair at your doorstep.`,
        numberOfItems: totalBrands,
        itemListElement: [...(brandsData.bike || []), ...(brandsData.car || [])].slice(0, 30).map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.label,
          url: `https://www.ridenrepair.com/brands/${b.slug}`,
        })),
      },
    ];

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>Brands We Service — Bike & Car Brands | Ride N Repair</title>
          <meta name="description" content={`Ride N Repair services ${totalBrands}+ bike and car brands at your doorstep. Hero, Honda, Bajaj, Royal Enfield, Maruti Suzuki, Hyundai, Tata, and more. Certified mechanics, genuine parts.`} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content="Brands We Service — Bike & Car Brands | Ride N Repair" />
          <meta property="og:description" content={`${totalBrands}+ brands serviced at your doorstep across 32+ cities.`} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schemas} />

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        <PremiumHero
          compact
          badge={`${totalBrands}+ Brands`}
          title="Brands We Service"
          subtitle="From Hero to Hyundai — doorstep service for every bike and car brand in India."
          isMobileBrowser={isMobileBrowser}
          showCarAnimation
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
        />

        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Brands' }]} />

        <StickyMobileCTA title="All Brands" priceHint={`Starting ${SITE_FROM} · 30-Day Warranty`} btnLabel="Book Now" btnHref="/service" />

        {/* SEARCH + FILTER */}
        <SectionWrapper>
          <div className="rr-brand-search">
            <input
              type="text"
              className="rr-brand-search__input"
              placeholder="Search brands..."
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
            />
          </div>
          <div className="rr-brand-type-toggle">
            {['all', 'bike', 'car'].map((f) => (
              <button
                key={f}
                className={`rr-brand-type-toggle__btn${filter === f ? ' rr-brand-type-toggle__btn--active' : ''}`}
                onClick={() => this.setState({ filter: f })}
              >
                {f === 'all' ? 'All' : f === 'bike' ? 'Bikes' : 'Cars'}
              </button>
            ))}
          </div>
        </SectionWrapper>

        {/* BIKE BRANDS */}
        {bikes.length > 0 && (
          <SectionWrapper kicker="Two-Wheelers" heading={`Bike Brands (${bikes.length})`}>
            <div className="rr-brand-grid">
              {bikes.map((b) => this.renderBrandCard(b))}
            </div>
          </SectionWrapper>
        )}

        {/* CAR BRANDS */}
        {cars.length > 0 && (
          <SectionWrapper variant="gray" kicker="Four-Wheelers" heading={`Car Brands (${cars.length})`}>
            <div className="rr-brand-grid">
              {cars.map((b) => this.renderBrandCard(b))}
            </div>
          </SectionWrapper>
        )}

        {bikes.length === 0 && cars.length === 0 && (
          <SectionWrapper>
            <p className="rr-text-center" style={{ color: '#999', fontSize: 16, padding: '48px 0' }}>
              No brands found for "{search}". Try a different search term.
            </p>
          </SectionWrapper>
        )}

        <FinalCTA
          heading={<>Need Vehicle Service?<br />Book Now — Get It Done Today.</>}
          subtitle={`Doorstep service · Certified mechanics · Starting ${SITE_FROM} · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default BrandsIndex;
